import React, { useState, useEffect } from "react";
import { MoreHorizontal } from "lucide-react";
import { ActionMenu, ActionMenuItem, AppBadge } from "../ui";
import apiClient from "../../utils/apiClient";
import DataTable from "../Common/DataTable";

const QuestionList = ({
  filters,
  refreshKey,
  onEdit,
  onDelete,
  mode = "institute",
  selectedIds = [],
  onSelectionChange,
}) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Institute users can't delete library questions; only own-institute rows
  // are selectable for bulk delete. In library mode (tenantadmin) all rows
  // are deletable.
  const isDeletable = (q) => mode === "library" || !!q.instituteId;
  const deletableIds = questions.filter(isDeletable).map((q) => q._id);
  const handleSelectionChange = (ids) => {
    if (typeof onSelectionChange !== "function") return;
    // Keep only IDs that are actually deletable (skip library rows for institute users).
    const allowed = new Set(deletableIds);
    onSelectionChange(ids.filter((id) => allowed.has(id)));
  };

  // Fetch paginated questions with filters
  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get("/questions/list", {
          params: {
            ...filters,
            page,
            limit: pageSize,
          },
        });
        setQuestions(data.questions);
        setTotalRecords(data.total || 0);
      } catch {
        setQuestions([]);
        setTotalRecords(0);
      }
      setLoading(false);
    };
    fetchQuestions();
  }, [filters, page, pageSize, refreshKey]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  // Unknown or missing difficulty keeps the "easy" tone, as it did before.
  const difficultyColor = (difficulty) => {
    if (difficulty === "Hard") return "rose";
    if (difficulty === "Medium") return "amber";
    return "emerald";
  };

  const columns = [
    {
      key: "index",
      label: "#",
      width: "50px",
      render: (_, index) => (page - 1) * pageSize + index + 1
    },
    {
      key: "title",
      label: "Title",
      width: "35%",
      render: (q) => (
        <div className="tw-font-medium" title={q.title}>
          {q.title?.length > 80 ? q.title.substring(0, 80) + "..." : q.title}
        </div>
      )
    },
    {
      key: "categories",
      label: "Categories",
      width: "15%",
      render: (q) => (
        <div className="tw-flex tw-flex-wrap tw-gap-1">
          {q.categories?.map((c) => (
            <AppBadge variant="secondary" key={typeof c === 'object' ? c.name : c}>{typeof c === 'object' ? c.name : c}</AppBadge>
          ))}
          {(!q.categories || q.categories.length === 0) && "-"}
        </div>
      )
    },
    {
      key: "labels",
      label: "Labels",
      width: "15%",
      render: (q) => (
        <div className="tw-flex tw-flex-wrap tw-gap-1">
          {q.labels?.map((l) => (
            <AppBadge color="blue" key={l}>{l}</AppBadge>
          ))}
          {(!q.labels || q.labels.length === 0) && "-"}
        </div>
      )
    },
    {
      key: "difficulty",
      label: "Difficulty",
      width: "10%",
      render: (q) => (
        <AppBadge color={difficultyColor(q.difficulty)}>{q.difficulty || "-"}</AppBadge>
      )
    },
    ...(mode === "library"
      ? [{
          key: "isPublic",
          label: "Visibility",
          width: "10%",
          render: (q) => (
            q.isPublic
              ? <AppBadge color="emerald">Public</AppBadge>
              : <AppBadge variant="secondary">Draft</AppBadge>
          )
        }]
      : [{
          key: "source",
          label: "Source",
          width: "10%",
          render: (q) => (
            !q.instituteId
              ? <AppBadge color="emerald">Library</AppBadge>
              : <AppBadge color="blue">My Institute</AppBadge>
          )
        }]),
    {
      key: "actions",
      label: "",
      width: "50px",
      render: (q) => {
        // Institute users see library questions read-only — hide edit/delete.
        if (mode !== "library" && !q.instituteId) {
          return <span className="tw-text-content-muted tw-text-sm">—</span>;
        }
        return (
          <ActionMenu trigger={<MoreHorizontal size={16} />}>
            <ActionMenuItem onClick={() => onEdit(q)}>Edit</ActionMenuItem>
            <ActionMenuItem danger onClick={() => onDelete(q._id)}>Delete</ActionMenuItem>
          </ActionMenu>
        );
      }
    }
  ];

  const selectable = typeof onSelectionChange === "function";

  return (
    <DataTable
      data={questions}
      columns={columns}
      loading={loading}
      hoverable={true}
      selectable={selectable}
      selectedRows={selectedIds}
      onSelectionChange={handleSelectionChange}
      pagination={{
        page,
        pageSize,
        total: totalRecords,
        onPageChange: setPage
      }}
      emptyMessage="No questions found"
    />
  );
};

export default QuestionList;
